/**
 * Extracts metadata from the root JSON object
 */
export function extractMetadata(root) {
  const runSettings = root.runSettings || null;
  const systemInstruction = root.systemInstruction || null;
  const title = inferTitleFromRoot(root);
  const date = inferDateFromRoot(root);
  return { title, date, runSettings, systemInstruction };
}

/**
 * Extracts messages from the JSON structure
 */
export function extractMessages(root, includeHidden) {
  const out = [];

  // Primary source: chunkedPrompt.chunks[]
  const chunks = root?.chunkedPrompt?.chunks || [];
  for (const c of chunks) {
    if (!c) continue;
    
    const role = normalizeRole(c.role);
    const isThought = Boolean(c.isThought);
    if (isThought && !includeHidden) continue;
    
    // If chunk has top-level text, use it (and skip parts to avoid duplication)
    if (typeof c.text === "string" && c.text.trim()) {
      out.push({ role, text: sanitizeText(c.text), isThought });
      continue; // Skip parts processing for this chunk
    }
    
    // Otherwise, extract from parts[] if available
    const parts = Array.isArray(c.parts) ? c.parts : [];
    for (const p of parts) {
      const partIsThought = Boolean(p.thought);
      if (partIsThought && !includeHidden) continue;
      if (typeof p.text === "string" && p.text.trim()) {
        out.push({ role, text: sanitizeText(p.text), isThought: partIsThought });
      }
    }
  }

  // Fallback: if there is a top-level transcript array
  if (Array.isArray(root.transcript)) {
    for (const t of root.transcript) {
      if (!t || typeof t.text !== "string") continue;
      const isThought = Boolean(t.isThought);
      if (isThought && !includeHidden) continue;
      out.push({ role: normalizeRole(t.role), text: sanitizeText(t.text), isThought });
    }
  }

  return out;
}

/**
 * Extracts grounding/reference information
 */
export function extractGrounding(root) {
  const refs = [];
  const chunks = root?.chunkedPrompt?.chunks || [];
  for (const c of chunks) {
    const g = c.grounding || c.groundedContent || c.groundingSources;
    if (!g) continue;
    const items = Array.isArray(g) ? g : Array.isArray(g?.groundingSources) ? g.groundingSources : [];
    for (const it of items) {
      const uri = it.uri || it.url || it.href;
      if (!uri) continue;
      refs.push({ uri, title: it.title || it.source || it.domain || undefined });
    }
  }
  // De-duplicate by uri
  const seen = new Set();
  return refs.filter((r) => (seen.has(r.uri) ? false : (seen.add(r.uri), true)));
}

// Helper functions

function normalizeRole(role) {
  if (!role) return "assistant";
  const r = String(role).toLowerCase();
  if (r === "model") return "assistant";
  if (r === "user") return "user";
  return r;
}

function sanitizeText(s) {
  // Preserve markdown content; just normalize newlines
  return String(s).replace(/\r\n?/g, "\n");
}

function inferTitleFromRoot(root) {
  return root.title || root.name || null;
}

function inferDateFromRoot(root) {
  return root.createdAt || root.timestamp || null;
}
